import {
  AppstoreOutlined,
  BellOutlined,
  ControlOutlined,
  DatabaseOutlined,
  EyeOutlined,
  FileTextOutlined,
  GlobalOutlined,
  SettingOutlined,
  TagsOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { BaseSidebar } from '@/components/layouts/base-sidebar';
import { SettingsBackNavigation } from '@/components/layouts/settings-back-navigation';
import type { MenuItem } from '@/components/layouts/sidebar-utils';
import { useProgressRouter } from '@/hooks/use-progress-router';
import { useScope } from '@/hooks/use-scope';

export function SettingsSidebar() {
  const { pathname } = useLocation();
  const router = useProgressRouter();
  const { t } = useTranslation();
  const { organizationSlug } = useScope();

  const orgBase = organizationSlug ? `/settings/org/${organizationSlug}` : null;

  const mainMenuItems: MenuItem[] = [
    {
      type: 'group',
      label: t('settings.groups.personal'),
      children: [
        {
          key: '/settings/profile',
          icon: <UserOutlined />,
          label: t('settings.profile'),
          onClick: () => router.push('/settings/profile'),
        },
      ],
    },
    {
      type: 'group',
      label: t('settings.groups.platform'),
      children: [
        {
          key: '/settings/country-configs',
          icon: <GlobalOutlined />,
          label: t('settings.countryConfigs'),
          onClick: () => router.push('/settings/country-configs'),
        },
        {
          key: '/settings/document-types',
          icon: <FileTextOutlined />,
          label: t('settings.documentTypes'),
          onClick: () => router.push('/settings/document-types'),
        },
      ],
    },
    ...(orgBase
      ? [
          {
            type: 'group' as const,
            label: t('settings.groups.organization'),
            children: [
              {
                key: 'org-system',
                icon: <SettingOutlined />,
                label: t('settings.tabs.system'),
                onClick: () => router.push(`${orgBase}/system`),
              },
              {
                key: 'org-display',
                icon: <EyeOutlined />,
                label: t('settings.tabs.display'),
                onClick: () => router.push(`${orgBase}/display`),
              },
              {
                key: 'org-notifications',
                icon: <BellOutlined />,
                label: t('settings.tabs.notifications'),
                onClick: () => router.push(`${orgBase}/notifications`),
              },
              {
                key: 'org-integrations',
                icon: <ControlOutlined />,
                label: t('settings.tabs.integrations'),
                onClick: () => router.push(`${orgBase}/integrations`),
              },
              {
                key: 'org-default-data',
                icon: <DatabaseOutlined />,
                label: t('settings.tabs.defaultData'),
                onClick: () => router.push(`${orgBase}/default-data`),
              },
              {
                key: 'org-event-types',
                icon: <TagsOutlined />,
                label: t('settings.tabs.eventTypes'),
                onClick: () => router.push(`${orgBase}/event-types`),
              },
              {
                key: 'org-service-types',
                icon: <AppstoreOutlined />,
                label: t('settings.tabs.serviceTypes'),
                onClick: () => router.push(`${orgBase}/service-types`),
              },
            ],
          },
        ]
      : []),
  ];

  const getSelectedKeys = () => {
    if (pathname.startsWith('/settings/profile')) return ['/settings/profile'];
    if (pathname.startsWith('/settings/country-configs'))
      return ['/settings/country-configs'];
    if (pathname.startsWith('/settings/document-types'))
      return ['/settings/document-types'];
    if (pathname.includes('/system')) return ['org-system'];
    if (pathname.includes('/display')) return ['org-display'];
    if (pathname.includes('/notifications')) return ['org-notifications'];
    if (pathname.includes('/integrations')) return ['org-integrations'];
    if (pathname.includes('/default-data')) return ['org-default-data'];
    if (pathname.includes('/event-types')) return ['org-event-types'];
    if (pathname.includes('/service-types')) return ['org-service-types'];
    return [pathname];
  };

  return (
    <BaseSidebar
      menuItems={mainMenuItems}
      getSelectedKeys={getSelectedKeys}
      renderTopSection={({ isCollapsed }) => (
        <SettingsBackNavigation isCollapsed={isCollapsed} />
      )}
    />
  );
}
